<template>
  <div
    v-if="attachments.length > 0 || uploading.length > 0"
    class="chat-attachments"
  >
    <!-- Completed attachments -->
    <div
      v-for="(attachment, index) in attachments"
      :key="'done-' + index"
      class="attachment-chip"
    >
      <img
        v-if="isImage(attachment.mime)"
        :src="attachment.url"
        :alt="attachment.name"
        class="attachment-thumbnail"
        @click.stop="openImagePreview(attachment.url)"
      />
      <span v-else class="attachment-file-icon">
        <FileIcon />
      </span>
      <span class="attachment-name">{{ attachment.name }}</span>
      <button class="attachment-remove" @click="$emit('remove', index)">
        <CloseIcon />
      </button>
    </div>

    <!-- Uploading attachments -->
    <div
      v-for="file in uploading"
      :key="'up-' + file.key"
      class="attachment-chip uploading"
    >
      <span class="attachment-file-icon">
        <FileIcon />
      </span>
      <span class="attachment-name">{{ file.name }}</span>
      <div class="upload-progress-track">
        <div
          class="upload-progress-bar"
          :style="{ width: file.progress + '%' }"
        />
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, h, ref, type PropType } from "vue";
import { useModal } from "vue-final-modal";

import CloseIcon from "@/components/icons/CloseIcon.vue";
import Modal from "@/components/modals/Modal.vue";

import FileIcon from "./FileIcon.vue";
import type { ChatAttachment, UploadingFile } from "./useAttachments";

export default defineComponent({
  name: "ChatAttachments",
  components: { CloseIcon, FileIcon },
  props: {
    attachments: {
      type: Array as PropType<ChatAttachment[]>,
      required: true,
    },
    uploading: {
      type: Array as PropType<UploadingFile[]>,
      default: () => [],
    },
  },
  emits: {
    remove: (_index: number) => true,
  },
  setup() {
    const isImage = (mime: string) => mime.startsWith("image/");

    const previewUrl = ref<string | null>(null);

    const { open: openPreviewModal } = useModal({
      component: Modal,
      attrs: {
        title: "",
        showTitleBar: false,
        showTopBar: false,
        showCancelButton: false,
        modalButtons: [],
        isCancellable: true,
      },
      slots: {
        default: () =>
          h("img", {
            src: previewUrl.value,
            alt: "Attachment preview",
            class: "attachment-preview-image",
          }),
      },
    });

    const openImagePreview = (url: string) => {
      previewUrl.value = url;
      openPreviewModal();
    };

    return { isImage, openImagePreview };
  },
});
</script>

<style scoped lang="scss">
@use "@/styles/_variables.scss" as *;
@use "@/styles/_themes.scss" as *;

.chat-attachments {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding-bottom: 8px;
}

.attachment-chip {
  display: flex;
  align-items: center;
  gap: 6px;
  padding-block: 2px;
  padding-inline-end: 8px;
  padding-inline-start: 2px;
  border-radius: $border-radius-m;
  max-width: 200px;

  @include themed() {
    background-color: t($grey-10);
    border: 1px solid t($grey-15);
  }
}

.attachment-thumbnail {
  width: 20px;
  height: 20px;
  object-fit: cover;
  border-radius: $border-radius-s;
  flex-shrink: 0;
  cursor: pointer;

  &:hover {
    opacity: 0.85;
  }
}

.attachment-file-icon {
  display: flex;
  align-items: center;
  flex-shrink: 0;
  margin-inline-start: 2px;
}

.attachment-name {
  @extend %font-small-regular;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;

  @include themed() {
    color: t($primary);
  }
}

.attachment-remove {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0;
  background: none;
  border: none;
  flex-shrink: 0;
  border-radius: $border-radius-s;
  opacity: 0.6;
  width: 12px;
  height: 12px;

  &:hover {
    opacity: 1;
  }
}

.attachment-chip.uploading {
  opacity: 0.7;
}

.upload-progress-track {
  flex: 1;
  min-width: 30px;
  height: 3px;
  border-radius: 2px;
  overflow: hidden;

  @include themed() {
    background-color: t($grey-15);
  }
}

.upload-progress-bar {
  height: 100%;
  border-radius: 2px;
  transition: width 0.15s ease-out;

  @include themed() {
    background-color: t($tertiary-50);
  }
}
</style>

<style lang="scss">
.attachment-preview-image {
  display: block;
  max-width: 100%;
  max-height: 75vh;
  border-radius: 8px;
  object-fit: contain;
}
</style>
